// components/common/AddToCartButton.tsx
import React, { useState } from 'react';
import { ShoppingCart, Package, Check, Loader2 } from 'lucide-react';
import { useCart } from '../../contexts/CartContext';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

interface AddToCartButtonProps {
  product: {
    id: number;
    title: string;
    selling_price: number;
    sku: string;
    images?: Array<{ image_url: string }>;
    vendor?: {
      id: number;
      business_name: string;
    };
    additional_images?: string[]; // Array of additional image URLs for the product
  };
  variants?: Array<{ id: number; name: string; full_sku?: string }>;
  availableQuantity?: number;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'primary' | 'secondary' | 'outline' | 'minimal';
  showText?: 'always' | 'desktop' | 'never';
  disabled?: boolean;
  pulseOnAdd?: boolean;
}

const AddToCartButton: React.FC<AddToCartButtonProps> = ({
  product,
  variants = [],
  availableQuantity = 0,
  className = '',
  size = 'md',
  variant = 'primary',
  showText = 'desktop',
  disabled = false,
  pulseOnAdd = true
}) => {
  const navigate = useNavigate();
  const { addItem, cart } = useCart();
  const [isAdding, setIsAdding] = useState(false);
  const [justAdded, setJustAdded] = useState(false);

  const hasMultipleVariants = variants.length > 1;
  const isOutOfStock = availableQuantity <= 0 && !hasMultipleVariants;
  const firstVariant = variants[0];
  const itemCount = cart?.items?.reduce((sum, item) => sum + item.quantity, 0) || 0;

  const handleAddToCart = async (e: React.MouseEvent) => {
    e.stopPropagation();

    if (hasMultipleVariants) {
      navigate(`/product/${product.id}`);
      return;
    }

    if (isOutOfStock || disabled) return;

    setIsAdding(true);
    try {
      
console.log('Adding to cart:', product);
      const allImages = product.images?.map(img => img.image_url) || [];
      await addItem({
        product_variant_id: firstVariant?.id || product.id,
        product_id: product.id,
        product_title: product.title,
        variant_name: firstVariant?.name || 'Standard',
        sku: firstVariant?.full_sku || product.sku,
        price: product.selling_price, // <-- This is 'price' in CartItem interface
        quantity: 1,
        image_url: product.images?.[0]?.image_url || undefined,
        vendor_id: product.vendor?.id || null,
        vendor_name: product.vendor?.business_name || '',
        additional_images: allImages,
      });

      // Success animation
      setJustAdded(true);

      // Success toast
      toast.success(
        <div className="flex items-center gap-3">
          <div className="relative">
            <div className="absolute inset-0 bg-green-500 rounded-full animate-ping opacity-75"></div>
            <Check className="h-5 w-5 text-white relative z-10" />
          </div>
          <div className="flex-1">
            <p className="font-semibold">Added to cart</p>
            <p className="text-sm opacity-90">{product.title}</p>
          </div>
          <div className="text-sm font-bold bg-white/20 px-2 py-1 rounded">
            {itemCount + 1}
          </div>
        </div>,
        {
          duration: 3000,
          position: 'bottom-right',
          style: {
            background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
            color: 'white',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(10px)',
          },
        }
      );

      // Reset success state after animation
      setTimeout(() => setJustAdded(false), 1000);

    } catch (error: any) {
      toast.error(
        <div className="flex items-center gap-2">
          <Package className="h-5 w-5" />
          <span>{error.message || 'Failed to add to cart'}</span>
        </div>,
        {
          duration: 4000,
          style: {
            background: '#EF4444',
            color: 'white',
          },
        }
      );
    } finally {
      setIsAdding(false);
    }
  };

  // Responsive text logic
  const shouldShowText = () => {
    if (showText === 'always') return true;
    if (showText === 'never') return false;
    return window.innerWidth >= 768; // 'desktop' - show on screens 768px+
  };

  const [showButtonText, setShowButtonText] = useState(shouldShowText());

  // Update on resize
  React.useEffect(() => {
    const handleResize = () => {
      if (showText === 'desktop') {
        setShowButtonText(window.innerWidth >= 768);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [showText]);

  // Size classes
  const sizeClasses = {
    sm: {
      button: 'px-3 py-1.5 text-sm h-8',
      icon: 'h-3.5 w-3.5',
      text: 'text-xs'
    },
    md: {
      button: 'px-4 py-2.5 text-sm h-10',
      icon: 'h-4 w-4',
      text: 'text-sm'
    },
    lg: {
      button: 'px-6 py-3 text-base h-12',
      icon: 'h-5 w-5',
      text: 'text-base'
    }
  };

  // Variant classes
  const variantClasses = {
    primary: 'bg-gradient-to-r from-blue-600 via-blue-500 to-blue-600 hover:from-blue-700 hover:via-blue-600 hover:to-blue-700 text-white shadow-lg hover:shadow-xl active:shadow-md border border-blue-700/20',
    secondary: 'bg-gradient-to-r from-gray-900 to-black hover:from-black hover:to-gray-900 text-white shadow-md hover:shadow-lg active:shadow-sm border border-gray-800/30',
    outline: 'bg-white text-gray-800 border-2 border-blue-500 hover:bg-blue-50 hover:border-blue-600 shadow-sm hover:shadow active:shadow-xs',
    minimal: 'bg-transparent text-blue-600 hover:bg-blue-50 border border-blue-200 hover:border-blue-300 shadow-xs hover:shadow-sm'
  };

  // Determine button text based on state
  const getButtonText = () => {
    if (isAdding) return 'Adding...';
    if (justAdded) return 'Added!';
    if (hasMultipleVariants) return 'View Options';
    if (isOutOfStock) return 'Out of Stock';
    return 'Add to Cart';
  };

  const buttonText = getButtonText();

  return (
    <button
      onClick={handleAddToCart}
      disabled={isOutOfStock || disabled || isAdding || availableQuantity === 0}
      className={`
        relative rounded-xl font-semibold flex items-center justify-center gap-2 
        transition-all duration-300 ease-out
        disabled:opacity-50 disabled:cursor-not-allowed
        active:scale-[0.98] transform-gpu
        hover:translate-y-[-1px] active:translate-y-0
        ${sizeClasses[size].button}
        ${variantClasses[variant]}
        ${className}
        ${justAdded && pulseOnAdd ? 'animate-pulse shadow-2xl shadow-blue-500/30' : ''}
      `}
      aria-label={hasMultipleVariants ? "View product options" : "Add to cart"}
    >
      {/* Animated background for success state */}
      {justAdded && (
        <div className="absolute inset-0 bg-gradient-to-r from-green-500 to-emerald-500 rounded-xl animate-pulse"></div>
      )}

      {/* Loading overlay */}
      {isAdding && (
        <div className="absolute inset-0 bg-white/20 backdrop-blur-sm rounded-xl"></div>
      )}

      <span className="relative z-10 flex items-center justify-center gap-2">
        {isAdding ? (
          <Loader2 className={`${sizeClasses[size].icon} animate-spin`} />
        ) : justAdded ? (
          <Check className={`${sizeClasses[size].icon}`} />
        ) : (
          <ShoppingCart className={`${sizeClasses[size].icon} ${!showButtonText ? 'mx-auto' : ''}`} />
        )}

        {showButtonText && (
          <span className={`${sizeClasses[size].text} whitespace-nowrap ${justAdded ? 'font-bold' : ''}`}>
            {buttonText}
          </span>
        )}
      </span>

      {/* Cart counter badge that animates on add */}
      {!isOutOfStock && !hasMultipleVariants && showButtonText && (
        <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center transform scale-0 group-hover:scale-100 transition-transform duration-300">
          +
        </span>
      )}
    </button>
  );
};

export default AddToCartButton;